/**
 * Общие константы. Значения, которые должен менять бизнес (лимиты, автопубликация,
 * тарифы), хранятся в таблице app_settings и редактируются в админ-панели;
 * здесь — только технические инварианты и значения по умолчанию.
 */

export const LOCALES = ['uz', 'kaa', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'uz';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Язык интерфейса по коду языка Telegram (для новых пользователей; одинаково на сервере и в Mini App). */
export function localeFromTelegram(code: string | undefined | null): Locale {
  if (!code) return DEFAULT_LOCALE;
  const c = code.toLowerCase();
  if (isLocale(c)) return c;
  if (c.startsWith('ru') || c === 'be' || c === 'uk' || c === 'kk') return 'ru';
  return DEFAULT_LOCALE;
}

/** Язык пользователя с учётом включённых языков: если его язык выключен — первый включённый. */
export function pickLocale(preferred: Locale, enabled: readonly Locale[]): Locale {
  return enabled.includes(preferred) ? preferred : (enabled[0] ?? DEFAULT_LOCALE);
}

export const LISTING_STATUSES = ['draft', 'pending', 'active', 'rejected', 'archived', 'deleted'] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const ARCHIVE_REASONS = ['user', 'sold', 'expired', 'admin'] as const;
export type ArchiveReason = (typeof ARCHIVE_REASONS)[number];

export const PRICE_TYPES = ['fixed', 'negotiable', 'free', 'exchange'] as const;
export type PriceType = (typeof PRICE_TYPES)[number];

export const ITEM_CONDITIONS = ['new', 'used'] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];

export const REPORT_REASONS = [
  'fraud',
  'prohibited',
  'wrong_info',
  'duplicate',
  'spam',
  'rules_violation',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_STATUSES = ['open', 'resolved', 'dismissed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const ATTRIBUTE_TYPES = ['text', 'number', 'select', 'boolean'] as const;
export type AttributeType = (typeof ATTRIBUTE_TYPES)[number];

export const LOCATION_TYPES = ['country', 'region', 'district', 'city', 'settlement'] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

export const SORT_OPTIONS = ['newest', 'price_asc', 'price_desc', 'relevance'] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];

/** Технические лимиты (не бизнес-настройки). */
export const LIMITS = {
  maxImagesPerListing: 10,
  maxImageBytes: 10 * 1024 * 1024,
  allowedImageMime: ['image/jpeg', 'image/png', 'image/webp'] as readonly string[],
  titleMin: 5,
  titleMax: 90,
  descriptionMin: 10,
  descriptionMax: 5000,
  /** 1 трлн сумов — защита от переполнения и опечаток. */
  maxPriceSum: 1_000_000_000_000,
  pageSizeDefault: 20,
  pageSizeMax: 50,
  searchQueryMax: 100,
  reportCommentMax: 1000,
  inquiryMessageMax: 500,
  chatMessageMax: 2000,
  recentSearchesKept: 10,
} as const;

/** Значения по умолчанию для app_settings (используются при первом запуске/seed). */
export const DEFAULT_SETTINGS = {
  autoPublish: false,
  maxActiveListingsPerUser: 50,
  dailyPublishLimit: 10,
  dailyReportLimit: 20,
  hourlyInquiryLimit: 10,
  listingTtlDays: 30,
  /** За сколько дней до истечения напомнить продавцу (0 — не напоминать). */
  expiryReminderDays: 3,
  /** Черновики без изменений дольше этого срока удаляются вместе с фото. */
  draftTtlDays: 90,
  /** Обозначение валюты в ценах по языкам (цены хранятся в минимальных единицах, 1 = 1/100). */
  currencyLabels: { uz: "so'm", kaa: 'swm', ru: 'сум' } as Record<Locale, string>,
  /** Языки интерфейса, доступные пользователям (настраивается администратором; минимум один). */
  enabledLocales: ['uz', 'kaa', 'ru'] as Locale[],
  /** Версия правил и политики; при изменении пользователи подтверждают согласие заново. */
  legalVersion: '2026-10',
  /**
   * Срок ответа на запрос удаления данных, дней: после него запрос подсвечивается как просроченный и
   * попадает в ежедневное оповещение. Значение согласуйте с юристом.
   */
  deletionResponseDays: 30,
  /** Требовать двухфакторный вход от всех администраторов (меняет только владелец/admins.manage). */
  adminTwoFactorRequired: false,
  bannedWords: [] as string[],
  promotionsEnabled: false,
  /**
   * Покупка продвижения пользователем за Telegram Stars (цифровая услуга внутри Mini App — по правилам Telegram только Stars).
   * Включает администратор после публикации оферты и правил возврата (docs/monetization.md).
   */
  paymentsEnabled: false,
  /**
   * Тарифы. priceSum — ориентир для ручных продаж (договор), priceStars — цена покупки в приложении (0 — не продаётся за Stars).
   * type: bump — поднятие, vip — VIP на durationDays, highlight — выделение на durationDays. Цены — гипотезы.
   */
  promotionTariffs: [
    { code: 'bump', type: 'bump', priceSum: 5_000, priceStars: 25, durationDays: 0 },
    { code: 'vip_3', type: 'vip', priceSum: 15_000, priceStars: 75, durationDays: 3 },
    { code: 'vip_7', type: 'vip', priceSum: 25_000, priceStars: 125, durationDays: 7 },
    { code: 'highlight_7', type: 'highlight', priceSum: 10_000, priceStars: 50, durationDays: 7 },
    { code: 'business_month', type: 'business', priceSum: 100_000, priceStars: 0, durationDays: 30 },
  ] as PromotionTariff[],
  /** Чат покупателя и продавца в приложении. */
  chatEnabled: true,
  /** Новых переписок, которые один пользователь может начать за час. */
  hourlyChatStartLimit: 20,
  /** Сообщений от одного пользователя в сутки (защита от рассылок). */
  dailyChatMessageLimit: 500,
  /**
   * Автопостинг одобренных объявлений в Telegram-канал площадки (бот должен быть администратором канала
   * с правом публиковать сообщения). channelChatId — @username канала или числовой id (-100…).
   */
  channelAutopost: false,
  channelChatId: '',
  /** Язык подписей в канале (цена, место, кнопка); текст объявления — на языке автора. */
  channelLocale: 'kaa' as Locale,
  /** Ежедневная сводка владельцу в Telegram (в чат оповещений) в 9:00 по Ташкенту. */
  dailyDigest: true,
};
export type AppSettings = typeof DEFAULT_SETTINGS;

export const PROMOTION_KINDS = ['bump', 'vip', 'highlight', 'business'] as const;
export type PromotionKind = (typeof PROMOTION_KINDS)[number];
export interface PromotionTariff {
  code: string;
  type: PromotionKind;
  priceSum: number;
  /** Цена в Telegram Stars; 0 — тариф не продаётся в приложении. */
  priceStars: number;
  durationDays: number;
}

/** Машиночитаемые коды ошибок API. Тексты для пользователя — в файлах локализации. */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHORIZED',
  'INIT_DATA_INVALID',
  'INIT_DATA_EXPIRED',
  'FORBIDDEN',
  'USER_BLOCKED',
  'CONSENT_REQUIRED',
  'TWO_FACTOR_REQUIRED',
  'TWO_FACTOR_SETUP_REQUIRED',
  'NOT_FOUND',
  'CONFLICT',
  'INVALID_STATUS_TRANSITION',
  'LISTING_INCOMPLETE',
  'DUPLICATE_LISTING',
  'LIMIT_REACHED',
  'RATE_LIMITED',
  'IMAGE_INVALID',
  'IMAGE_TOO_LARGE',
  'IMAGE_LIMIT_REACHED',
  'CONTACT_UNAVAILABLE',
  'CHAT_BLOCKED',
  'PAYMENT_FAILED',
  'TELEGRAM_UNAVAILABLE',
  'AUDIO_INVALID',
  'FEATURE_DISABLED',
  'INTERNAL_ERROR',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
